import * as zod from 'zod';

import { Expression } from '../../types';

/**
 * Where a vision step writes the frame it ran on, and whether it writes a
 * record beside it.
 *
 * Its own file rather than a member of `captureUtils`, because `Arguments.ts`
 * schemas are the one thing that must stay importable from a browser bundle:
 * `captureUtils` reaches for `fs`, `os` and `sharp`, and folding this in would
 * drag all three into every consumer of a step's argument schema.
 *
 * There were FOUR hand-written copies of this shape, in two layers: the runner's
 * `Locate` and `RunLocateSkill` argument schemas, and the editor-side
 * `libs/remote-control/step/{locate,runLocateSkill}.ts` that mirror them and
 * forward `captureConfig` wholesale through `toRoutineRunner`. All four are now
 * this one schema.
 *
 * The editor layer is the half that makes the duplication dangerous rather than
 * merely untidy. A zod object strips unknown keys, so a field added here and to
 * the runner but NOT to the editor schema is parsed away before
 * `toRoutineRunner` ever sees it — the operator configures something, the editor
 * saves it, and the runner is handed a config without it. The Data plane adds a
 * second write target to this shape, so that is a target a routine displays and
 * never writes to.
 *
 * The Capture STEP is deliberately not one of the four. It carries these same
 * three field names FLAT on its own arguments rather than nested under a
 * `captureConfig`, in both layers, so sharing this would mean either nesting
 * them (which breaks every saved Capture step) or spreading `.shape`. A field
 * added here therefore does NOT reach the Capture step; that step wants its own
 * decision about whether it takes several targets.
 */
/**
 * One place a capture writes, beyond the legacy single `saveEquipmentId`.
 *
 * A local target carries NO path. The directory is robot configuration, not
 * routine configuration, and that split is the containment boundary rather than
 * a matter of taste: `saveFilepath` is an operator-authored expression that
 * validation only checks is a non-empty string ending in `.png`, so a routine
 * that could name its own local directory could write into `/etc` or fill the
 * root filesystem. An FTP or S3 target is bounded by its equipment; a directory
 * on the robot has no such boundary, so the routine says only THAT it writes
 * locally and the robot says where.
 */
export const CaptureTarget = zod.discriminatedUnion('kind', [
  zod.object({
    kind: zod.literal('equipment'),
    /** An `FTPServer` or `S3Bucket` equipment id. */
    equipmentId: zod.string(),
  }),
  zod.object({
    kind: zod.literal('local'),
  }),
]);

export type CaptureTarget = zod.infer<typeof CaptureTarget>;

/**
 * One part of a frame's filename, read when the step plays. `stepNumber` is
 * written `Step_<n>`, and `{name}` in text fills in a routine variable.
 */
export const FileNamePart = zod.discriminatedUnion('kind', [
  zod.object({ kind: zod.literal('stepNumber') }),
  zod.object({ kind: zod.literal('stepName') }),
  zod.object({ kind: zod.literal('variable'), variableID: zod.string() }),
  zod.object({
    kind: zod.literal('stepVariable'),
    stepID: zod.string(),
    name: zod.string(),
  }),
  zod.object({ kind: zod.literal('text'), value: zod.string() }),
]);

export type FileNamePart = zod.infer<typeof FileNamePart>;

/** The parts the editor names a capture with when it turns one on. */
export const NEW_CAPTURE_FILE_NAME: FileNamePart[] = [{ kind: 'stepNumber' }];

export const CaptureConfig = zod.object({
  saveEquipmentId: zod.string(),
  /**
   * Additional destinations, beside the legacy `saveEquipmentId`.
   *
   * `saveEquipmentId` stays because every saved routine has one and the editor
   * still writes it. The effective destination list is that field plus these,
   * so an absent or empty array means exactly today's behaviour.
   *
   * `.optional()` rather than `.default([])`, which was the first attempt and
   * was wrong twice over. A default types the inferred field as REQUIRED, which
   * (a) makes every object literal constructing a `captureConfig` fail unless
   * it names `destinations` — breaking two editor call sites that predate the field
   * — and (b) tells the compiler a runtime guard on it is dead code. It is not:
   * a step's `this.args` is the raw stored configuration, so a zod default
   * never applies at play time and a config saved before this field existed has
   * no key at all. Optional describes what the data actually is, and leaves the
   * guard reading as necessary because it is.
   *
   * Named `destinations` rather than `targets` because agent-bot's routine
   * snapshot drops any key called `targets` wherever it appears: it classifies
   * pose containers by key name alone, at any depth, so a capture destination
   * list under that name disappears from every routine diff with no error
   * anywhere. `routineSnapshot.ts` is the dropper and the drift guard in
   * `apps/agent-bot/src/observability/__tests__` is what catches the collision.
   */
  destinations: zod.array(CaptureTarget).optional(),
  /**
   * Where to write the frame, as an expression the routine evaluates.
   *
   * OPTIONAL, and absence is the auto case: the runner generates a unique
   * filename instead. An expression that IS set always wins, so no routine
   * written before this was optional changes behaviour.
   *
   * A generated name is a bare filename, never a path, which is what makes the
   * default safe on every transport. `FTPServer.uploadFile` joins what it is
   * given onto its configured `basePath`, `S3Bucket` prefixes `keyPrefix`, and
   * a local target joins its configured directory — so one filename lands
   * inside whatever base the target already defines.
   *
   * Absence and an explicitly EMPTY expression are deliberately different
   * things. An empty one is still an error: silently generating a path over it
   * would present an operator's unfinished config as though it had taken
   * effect.
   */
  saveFilepath: Expression.optional(),
  saveMetadata: zod.boolean().default(false),
  /**
   * Parts added after `<kind>_<skill>_<timestamp>` in the frame's name. With
   * none, captures keep their usual names.
   */
  fileName: zod.array(FileNamePart).optional(),
});

export type CaptureConfig = zod.infer<typeof CaptureConfig>;
